import { useNavigate } from "@tanstack/react-router";
import { Button, Spinner } from "@cntrl/ui";
import { useAdsCampaigns, type AdCampaignView } from "../../../app/hooks/useAds.js";
import { compactOrDash, ctr, flightDates, moneyOrDash, pctOrDash } from "../../marketing/marketingFormat.js";
import { SectionRail } from "../sectionRail.js";
import styles from "../section.module.scss";
import own from "./LiveAdsSection.module.scss";

// Live Ads (shot-13). "+ Boost this release" opens the Ad Creator pre-set to a release-promote, which tags the
// created Meta campaign to this release (ad_campaign_tags).
//
// The campaigns are the Ads Manager's OWN read, filtered to this release (ART-276/275). We store no campaign
// names or figures — they are Meta's, read live — so this tab and the Marketing campaigns table render the same
// rows from one request (one cache entry, too: the default account, the key the table opens on) and cannot
// disagree about a campaign's status or numbers. The release document used to carry a hard-coded empty campaign
// list here, which is why a tagged campaign never appeared; that shell is gone.
//
// Known limit: the read covers the band's DEFAULT ad account. A campaign tagged from another account shows on
// the table when that account is selected there, and not here.

export function LiveAdsSection({ releaseId }: { releaseId: string }) {
  const navigate = useNavigate();
  const q = useAdsCampaigns(null, true);
  const openAdCreator = () =>
    void navigate({ to: "/marketing/campaigns/new", search: { release: releaseId } });

  const rail = (
    <SectionRail>
      {/* The rail's ordinary button, as every other section uses — a bespoke pill here had kept the disabled
          look (grey ink, not-allowed cursor) from when Boost really was disabled. */}
      <Button size="sm" onClick={openAdCreator}>
        + Boost this release
      </Button>
    </SectionRail>
  );

  if (q.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading ads…" />
      </div>
    );
  }

  const status = q.data?.apiStatus;
  const campaigns = (q.data?.campaigns ?? []).filter((c) => c.releaseId === releaseId);

  let body;
  if (q.isError) {
    body = (
      <div className={styles.placeholder}>
        <p className={styles.placeholderTitle}>Couldn't load campaigns</p>
        <button type="button" className={own.retry} onClick={() => void q.refetch()}>
          Try again
        </button>
      </div>
    );
  } else if (status?.state !== "ok") {
    // Not connected / missing permission / Meta down — the Marketing status strip's three sentences. A bare
    // "no campaigns" here would claim something we could not check.
    body = (
      <div className={styles.placeholder}>
        <p className={styles.placeholderTitle}>{unavailableLine(status?.state)}</p>
      </div>
    );
  } else if (campaigns.length === 0) {
    body = (
      <div className={styles.placeholder}>
        {/* The explanatory body ("Boost this release to create a Meta campaign…") was removed per Ryan
            (ART-201) — the "+ Boost this release" action above says what this does. */}
        <p className={styles.placeholderTitle}>No campaigns tagged yet</p>
      </div>
    );
  } else {
    body = (
      <div className={own.cards}>
        {campaigns.map((c) => (
          <CampaignCard key={c.id} c={c} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.section}>
      {rail}
      {body}
    </div>
  );
}

function unavailableLine(state: string | null | undefined): string {
  if (state === "not-connected") return "Connect Meta on the Marketing page to see campaigns";
  if (state === "ads-permission-missing") return "Reconnect Meta with ad permissions to see campaigns";
  return "Meta is temporarily unavailable — campaigns couldn't load";
}

// One full-width row per campaign (ART-275 feedback): thumbnail · name + status + flight + spend · the three
// counts right-aligned. A Meta campaign name is often a pasted URL with no spaces, so the name truncates on one
// line (full text on hover) instead of overflowing the row — that overflow is what the old grid card did.
function CampaignCard({ c }: { c: AdCampaignView }) {
  const state = c.state ?? "draft";
  const name = c.name || c.id || "Campaign";
  // `spend` ABSENT ⇒ this seat holds no marketing money, so the line is dropped (not dashed) — the table's rule.
  const spend = c.spend;
  return (
    <div className={own.card}>
      <span className={own.thumb} aria-hidden="true">
        {c.thumbnailUrl ? (
          <img className={own.thumbImg} src={c.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <span className={own.thumbLetter}>{name.charAt(0).toUpperCase()}</span>
        )}
      </span>
      <div className={own.main}>
        <div className={own.cardHead}>
          <span className={own.cardName} title={name}>
            {name}
          </span>
          <span className={own.cardStatus}>{state}</span>
        </div>
        <p className={own.cardDates}>{flightDates(c.startTime, c.stopTime)}</p>
        {spend !== undefined && (
          <p className={own.cardSpend}>
            Spend · <strong>{moneyOrDash(spend?.spendCents)}</strong>
          </p>
        )}
      </div>
      <dl className={own.stats}>
        {/* "Results" is link clicks, as on the table — Meta's insights carry no dedicated results field. */}
        <Stat label="Results" value={compactOrDash(c.clicks)} />
        <Stat label="CTR" value={pctOrDash(ctr(c.clicks, c.impressions))} />
        <Stat label="Reach" value={compactOrDash(c.reach)} />
      </dl>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className={own.stat}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

